import React, { useMemo } from "react";
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	IconButton,
	useMediaQuery,
	useTheme,
} from "@material-ui/core";
import CloseIcon from "@material-ui/icons/Close";
import PictureAsPdfIcon from "@material-ui/icons/PictureAsPdf";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import GetAppIcon from "@material-ui/icons/GetApp";
import ErrorOutlineIcon from "@material-ui/icons/ErrorOutline";
import { useIntl } from "react-intl";

import MaterialUI from "../MaterialUI";
import { Loader } from "../loader";
import "./modalPdf.css";

const PDF_DOWNLOAD_NAME = "Tech4Shops-UserGuide.pdf";

function getEmbedPdfUrl(pdfUrl) {
	if (!pdfUrl) return "";
	const hash = "view=FitH&toolbar=1";
	return pdfUrl.includes("#") ? pdfUrl : `${pdfUrl}#${hash}`;
}

function PdfMobileViewer({ title, pdfUrl, trans }) {
	return (
		<div className="modal__pdf-mobile">
			<div className="modal__pdf-mobile-card">
				<div className="modal__pdf-mobile-icon" aria-hidden="true">
					<PictureAsPdfIcon />
				</div>
				<h3 className="modal__pdf-mobile-title">{title}</h3>
				<p className="modal__pdf-mobile-hint">
					{trans({
						id: "help_pdf_mobile_hint",
						defaultMessage:
							"Mobile browsers often cannot show PDFs inside the app. Open the guide in your browser for the best reading experience.",
					})}
				</p>
				<div className="modal__pdf-mobile-actions">
					<MaterialUI.Button.Primary
						className="modal__pdf-mobile-btn"
						component="a"
						href={pdfUrl}
						target="_blank"
						rel="noopener noreferrer"
					>
						<OpenInNewIcon fontSize="small" className="me-2" />
						{trans({
							id: "view_user_guide",
							defaultMessage: "View guide",
						})}
					</MaterialUI.Button.Primary>
					<MaterialUI.Button.Secondary
						className="modal__pdf-mobile-btn"
						variant="outlined"
						component="a"
						href={pdfUrl}
						download={PDF_DOWNLOAD_NAME}
					>
						<GetAppIcon fontSize="small" className="me-2" />
						{trans({ id: "download", defaultMessage: "Download" })}
					</MaterialUI.Button.Secondary>
				</div>
			</div>
		</div>
	);
}

function PdfErrorState({ message, trans }) {
	return (
		<div className="modal__pdf-error">
			<ErrorOutlineIcon className="modal__pdf-error-icon" />
			<p>
				{message ||
					trans({
						id: "help_pdf_load_error",
						defaultMessage: "Could not load the user guide.",
					})}
			</p>
		</div>
	);
}

const PDFModal = ({
	isOpen,
	closeModal,
	onExited,
	title,
	pdfUrl,
	isLoading,
	loadError,
}) => {
	const { formatMessage: trans } = useIntl();
	const theme = useTheme();
	const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
	const embedUrl = useMemo(() => getEmbedPdfUrl(pdfUrl), [pdfUrl]);
	const hasPdf = Boolean(pdfUrl) && !loadError;

	return (
		<Dialog
			open={isOpen}
			onClose={closeModal}
			maxWidth="xl"
			fullWidth
			fullScreen={isMobile}
			className="modal__pdf"
			classes={{ paper: "modal__pdf-paper" }}
			aria-labelledby="modal-pdf-title"
			TransitionProps={{
				onExited: () => {
					onExited?.();
				},
			}}
		>
			<DialogTitle
				className="modal__pdf-header"
				disableTypography
				id="modal-pdf-title"
			>
				<span className="modal__pdf-title">{title}</span>
				<IconButton
					onClick={closeModal}
					aria-label={trans({ id: "close", defaultMessage: "Close" })}
					className="modal__pdf-close"
					size="small"
				>
					<CloseIcon fontSize="small" />
				</IconButton>
			</DialogTitle>

			<DialogContent className="modal__pdf-body">
				{isLoading ? (
					<div className="modal__pdf-loading">
						<Loader variant="inline" showMessage={false} />
					</div>
				) : loadError ? (
					<PdfErrorState message={loadError} trans={trans} />
				) : isMobile && hasPdf ? (
					<PdfMobileViewer
						title={title}
						pdfUrl={pdfUrl}
						trans={trans}
					/>
				) : hasPdf ? (
					<iframe
						src={embedUrl}
						title={title}
						className="modal__pdf-iframe"
					/>
				) : null}
			</DialogContent>

			<DialogActions className="modal__pdf-footer">
				{!isMobile && hasPdf && !isLoading && (
					<MaterialUI.Button.Primary
						variant="outlined"
						component="a"
						href={pdfUrl}
						target="_blank"
						rel="noopener noreferrer"
					>
						{trans({
							id: "open_in_new_tab",
							defaultMessage: "Open in new tab",
						})}
					</MaterialUI.Button.Primary>
				)}
				<MaterialUI.Button.Secondary
					variant={isMobile ? "contained" : "outlined"}
					className={isMobile ? "modal__pdf-footer-close" : undefined}
					onClick={closeModal}
				>
					{trans({ id: "close", defaultMessage: "Close" })}
				</MaterialUI.Button.Secondary>
			</DialogActions>
		</Dialog>
	);
};

export default PDFModal;
